/**
 * Admin → Settings → Maestro Actuals (all projects) — opens as a modal.
 *
 * One Maestro export with every job's hours → weekly spent rows for each job
 * that exists in the portal (window replace per job). Shows the
 * reconciliation summary, the SharePoint drop folder state, and recent runs.
 * Never changes a project's Spent source by itself — the "Use Maestro" button
 * on a suggested row is an explicit admin action.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';

const ACCENT = '#0f766e';
const GUIDE_ID = 'maestro-actuals-import-help';

const FLAG_TEXT = {
  not_in_portal: { text: 'Not in portal — hours not imported', tone: 'warn' },
  ambiguous_portal_match: { text: 'Several portal projects match — not imported', tone: 'warn' },
  source_auto_has_maestro_hours: { text: 'Spent source is "auto" — EVM will not read these hours until it is set to Maestro', tone: 'warn' },
  source_time_tracker: { text: 'Spent source is Time Tracker — hours stored, EVM reads the Time Tracker', tone: 'info' },
  first_import: { text: 'First import for this job', tone: 'info' },
  file_lower_than_seeded: { text: 'File has fewer hours than were seeded in this window', tone: 'warn' },
  history_kept_before_window: { text: 'Older history before the window was kept', tone: 'info' },
  rows_kept_after_window: { text: 'Rows after the window were kept', tone: 'info' },
};

const fmtH = (n) => (n == null ? '—' : Number(n).toLocaleString('en-CA', { maximumFractionDigits: 0 }));
const fmtDt = (s) => (s ? new Date(s).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const SOURCE_LABEL = { auto: 'Auto', time_tracker: 'Time Tracker', maestro_dump: 'Maestro dump' };

const card = {
  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
  padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const btn = (primary, disabled) => ({
  padding: '0.5rem 1rem', borderRadius: 6, fontSize: '0.9rem', cursor: disabled ? 'wait' : 'pointer',
  border: primary ? 'none' : '1px solid #cbd5e1',
  background: primary ? ACCENT : '#fff', color: primary ? '#fff' : '#0f172a',
  opacity: disabled ? 0.6 : 1,
});
const th = { padding: '6px 8px', textAlign: 'left', fontSize: 12, color: '#475569', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

function Delta({ value }) {
  if (value == null) return <span>—</span>;
  const v = Number(value);
  const color = v < 0 ? '#b91c1c' : v > 0 ? '#047857' : '#6b7280';
  return <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>{v > 0 ? '+' : ''}{fmtH(v)}</span>;
}

function SummaryTable({ summary, onUseMaestro, busyJob }) {
  if (!summary) return null;
  const headline = summary.dryRun
    ? `Preview only — nothing written. ${summary.jobsInFile} job(s) in file, ${summary.jobsSkipped} not in portal.`
    : `${summary.jobsImported} job(s) imported (${fmtH(summary.hoursImported)} h), ${summary.jobsSkipped} not in portal${summary.jobsErrored ? `, ${summary.jobsErrored} error(s)` : ''}.`;
  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ fontSize: 13, color: '#374151', marginBottom: 6 }}>
        <strong>{summary.filename}</strong> · weeks <strong>{summary.fileMinWeek}</strong> → <strong>{summary.fileMaxWeek}</strong> · {fmtH(summary.fileHours)} h in file · {headline}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 860 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={th}>Job</th>
              <th style={th}>Window in file</th>
              <th style={{ ...th, textAlign: 'right' }}>File h</th>
              <th style={{ ...th, textAlign: 'right' }}>Was seeded (window)</th>
              <th style={{ ...th, textAlign: 'right' }}>Δ</th>
              <th style={th}>Spent source</th>
              <th style={th}>Result</th>
            </tr>
          </thead>
          <tbody>
            {(summary.jobs || []).map((j) => {
              const flags = (j.flags || []).map((f) => FLAG_TEXT[f] || { text: f, tone: 'info' });
              const suggest = (j.flags || []).includes('source_auto_has_maestro_hours') && j.action === 'imported';
              const resultColor = j.action === 'imported' ? '#047857' : j.action === 'error' ? '#b91c1c' : j.action === 'preview' ? '#2563eb' : '#6b7280';
              return (
                <tr key={j.maestroJob}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{j.portalJobNumber || j.maestroJob}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {j.jobName || (j.projectCodes || []).join(' / ')}
                      {j.estimateNumber ? ` · ${j.estimateNumber}` : ''}
                      {j.portalJobNumber && j.portalJobNumber !== j.maestroJob ? ` · Maestro ${(j.projectCodes || []).join('/')}` : ''}
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {j.minWeek} → {j.maxWeek}
                    <div style={{ fontSize: 12, color: '#64748b' }}>{j.weeks} wk · {(j.disciplines || []).join(' ')}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtH(j.fileHours)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtH(j.seededInWindowHours)}</td>
                  <td style={{ ...td, textAlign: 'right' }}><Delta value={j.deltaHours} /></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {j.actualsSource ? SOURCE_LABEL[j.actualsSource] || j.actualsSource : '—'}
                    {suggest && onUseMaestro && (
                      <div>
                        <button
                          type="button"
                          onClick={() => onUseMaestro(j.portalJobNumber)}
                          disabled={busyJob === j.portalJobNumber}
                          style={{ ...btn(false, busyJob === j.portalJobNumber), padding: '2px 8px', fontSize: 12, marginTop: 4 }}
                          title="Set this project's Spent source to Maestro dump so the EVM report reads the imported hours"
                        >
                          {busyJob === j.portalJobNumber ? 'Saving…' : 'Use Maestro for EVM'}
                        </button>
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <span style={{ color: resultColor, fontWeight: 600 }}>{j.action}</span>
                    {j.error && <div style={{ color: '#b91c1c', fontSize: 12 }}>{j.error}</div>}
                    {flags.map((f, i) => (
                      <div key={i} style={{ fontSize: 12, color: f.tone === 'warn' ? '#92400e' : '#64748b' }}>{f.text}</div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HelpModal({ onClose }) {
  const h4 = { margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.4 };
  const p = { margin: '0 0 12px', fontSize: 13, color: '#334155', lineHeight: 1.5 };
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10050, padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.28)' }}>
        <div style={{ padding: '14px 18px', background: ACCENT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Maestro actuals — all projects</h3>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>One export · every job · SharePoint drop folder</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId={GUIDE_ID} title="Maestro Actuals Import Help" accent={ACCENT} dark />
            <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ padding: '18px 20px 22px', overflowY: 'auto', flex: 1 }}>
          <HelpWithVideoTour guideId={GUIDE_ID} title="Maestro Actuals Import Help" accent={ACCENT}>
            <h4 style={h4}>What it does</h4>
            <p style={p}>
              Export every project's labour hours from Maestro into one Excel file (Date, Project, Activity, Time, Group) and import it once here. The portal splits the file by job number, matches each job to a portal project (<code>71923STR</code> in Maestro matches the portal project <code>71923STR</code> or <code>71923</code>), and loads the weekly spent hours used by the Resource Histogram and the EVM report.
            </p>
            <h4 style={h4}>Window replace, not full replace</h4>
            <p style={p}>
              Maestro exports cover a date range. For each job, only the weeks between that job's first and last week in the file are deleted and rewritten; older history stays. That makes a rolling export safe to re-import every week. To wipe and rebuild a single job from scratch, use <strong>Upload Maestro</strong> on that project's EVM report instead.
            </p>
            <h4 style={h4}>Preview first</h4>
            <p style={p}>
              <strong>Preview</strong> parses the file and shows, per job, the hours in the file against what is already seeded for the same weeks — nothing is written. <strong>Import</strong> writes it. Jobs that are not in the portal are listed, never imported; create the project under Admin → Projects and import again.
            </p>
            <h4 style={h4}>Spent source is never changed for you</h4>
            <p style={p}>
              The EVM report only reads Maestro hours when the project's Spent source is <strong>Maestro dump</strong>. Projects still on <strong>Auto</strong> that now have Maestro hours are flagged with a <strong>Use Maestro for EVM</strong> button — click it per project when you are ready. Time Tracker projects keep reading the Time Tracker.
            </p>
            <h4 style={h4}>SharePoint drop folder</h4>
            <p style={p}>
              Drop the export in <strong>PMO › Portal › Resource Histogram › Maestro Actuals</strong> and the portal imports it every night at three AM and e-mails the same reconciliation to the <em>Maestro Actuals Import Summary</em> roster (Admin → Notifications). A file is imported once; saving a new version of the same file imports it again. <strong>Check folder now</strong> runs the pickup immediately.
            </p>
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
}

const MaestroActualsImportPanel = () => {
  const { instance, accounts } = useMsal();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(null); // 'preview' | 'import' | 'sync' | null
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sharepoint, setSharepoint] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busyJob, setBusyJob] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const loadSide = useCallback(async () => {
    const [sp, b] = await Promise.all([
      authenticatedApiCall(instance, accounts, 'GET', '/resource-histogram/maestro-actuals/sharepoint', null, { timeout: 60000 }).catch(() => null),
      authenticatedApiCall(instance, accounts, 'GET', '/resource-histogram/maestro-actuals/batches?limit=15').catch(() => null),
    ]);
    if (sp?.data?.success) setSharepoint(sp.data);
    if (b?.data?.success) setBatches(b.data.batches || []);
  }, [instance, accounts]);

  useEffect(() => { loadSide(); }, [loadSide]);

  const run = async (dryRun) => {
    if (!file) return;
    setBusy(dryRun ? 'preview' : 'import');
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/resource-histogram/maestro-actuals/all?dryRun=${dryRun ? '1' : '0'}`,
        fd, { timeout: 300000 }
      );
      setSummary(res?.data?.summary || null);
      if (!dryRun) loadSide();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Import failed');
    } finally {
      setBusy(null);
    }
  };

  const syncNow = async () => {
    setBusy('sync');
    setError(null);
    setSyncResult(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', '/resource-histogram/maestro-actuals/sharepoint/sync', null, { timeout: 300000 });
      setSyncResult(res?.data || null);
      const first = (res?.data?.results || []).find((r) => r.batchId);
      if (first) {
        const s = await authenticatedApiCall(instance, accounts, 'GET', `/resource-histogram/maestro-actuals/batches/${first.batchId}`).catch(() => null);
        if (s?.data?.summary) setSummary(s.data.summary);
      }
      loadSide();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Sync failed');
    } finally {
      setBusy(null);
    }
  };

  const openBatch = async (batchId) => {
    setError(null);
    try {
      const s = await authenticatedApiCall(instance, accounts, 'GET', `/resource-histogram/maestro-actuals/batches/${batchId}`);
      if (s?.data?.summary) setSummary(s.data.summary);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  const useMaestro = async (jobNumber) => {
    if (!jobNumber) return;
    if (!window.confirm(`Set ${jobNumber} Spent source to Maestro dump? The EVM report will read the imported Maestro hours for this project.`)) return;
    setBusyJob(jobNumber);
    try {
      await authenticatedApiCall(instance, accounts, 'PATCH', `/projects/by-job/${encodeURIComponent(jobNumber)}/actuals-source`, { actuals_source: 'maestro_dump' });
      setSummary((prev) => prev ? {
        ...prev,
        jobs: prev.jobs.map((j) => (j.portalJobNumber === jobNumber
          ? { ...j, actualsSource: 'maestro_dump', flags: (j.flags || []).filter((f) => f !== 'source_auto_has_maestro_hours') }
          : j)),
      } : prev);
    } catch (err) {
      window.alert(err?.response?.status === 403 ? 'Only a project admin can change the spent feed.' : (err?.response?.data?.message || 'Could not update spent feed.'));
    } finally {
      setBusyJob(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>Maestro actuals — all projects</h3>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            One Maestro export, every job. Only the weeks in the file are replaced per job; Spent source is never changed automatically.
          </div>
        </div>
        <button type="button" onClick={() => setShowHelp(true)} style={{ ...btn(false), display: 'inline-flex', gap: 6, alignItems: 'center', marginRight: 28 }} title="How this import works">
          <span aria-hidden>❓</span> Help
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: '1rem', padding: '0.6rem 0.9rem', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, fontSize: 13 }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: 15 }}>Upload an export</h4>
          <p style={{ margin: '0 0 0.75rem', fontSize: 13, color: '#475569' }}>
            Raw Maestro labour export with Date, Project (e.g. 75998ELE), Activity, Time and Group columns. Premium and non-labour rows are skipped.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setSummary(null); setError(null); }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => fileRef.current?.click()} style={btn(false)}>Choose file…</button>
            <span style={{ fontSize: 13, color: file ? '#0f172a' : '#94a3b8', flex: 1, minWidth: 120 }}>{file ? file.name : 'No file chosen'}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" disabled={!file || !!busy} onClick={() => run(true)} style={btn(false, busy === 'preview')}>
              {busy === 'preview' ? 'Reading…' : 'Preview (no changes)'}
            </button>
            <button type="button" disabled={!file || !!busy} onClick={() => run(false)} style={btn(true, busy === 'import')}>
              {busy === 'import' ? 'Importing…' : 'Import'}
            </button>
          </div>
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: 15 }}>SharePoint drop folder</h4>
          <p style={{ margin: '0 0 0.5rem', fontSize: 13, color: '#475569' }}>
            Nightly 3:00 AM pickup from{' '}
            {sharepoint?.folder?.webUrl
              ? <a href={sharepoint.folder.webUrl} target="_blank" rel="noopener noreferrer">PMO › Portal › {sharepoint.folder.path}</a>
              : <span>PMO › Portal › Resource Histogram › Maestro Actuals</span>}
            . Summary e-mail goes to the <em>Maestro Actuals Import Summary</em> roster (Admin → Notifications).
          </p>
          <button type="button" disabled={!!busy} onClick={syncNow} style={btn(true, busy === 'sync')}>
            {busy === 'sync' ? 'Checking…' : 'Check folder now'}
          </button>
          {syncResult && (
            <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>
              {syncResult.filesProcessed === 0
                ? `Nothing new — ${syncResult.filesInFolder} file(s) already processed.`
                : `${syncResult.filesProcessed} file(s) processed${syncResult.email?.sent ? ` · summary e-mailed to ${syncResult.email.recipients}` : ' · no e-mail recipients subscribed'}.`}
            </div>
          )}
          {sharepoint?.files?.length > 0 && (
            <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 10 }}>
              <thead><tr><th style={th}>File</th><th style={th}>Modified</th><th style={th}>Status</th></tr></thead>
              <tbody>
                {sharepoint.files.slice(0, 8).map((f) => (
                  <tr key={f.driveItemId}>
                    <td style={td}><a href={f.webUrl} target="_blank" rel="noopener noreferrer">{f.fileName}</a></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDt(f.modifiedAt)}</td>
                    <td style={td}>
                      <span style={{ color: f.status === 'imported' ? '#047857' : f.status === 'error' ? '#b91c1c' : '#2563eb', fontWeight: 600 }}>{f.status}</span>
                      {f.batchId && <button type="button" onClick={() => openBatch(f.batchId)} style={{ ...btn(false), padding: '1px 6px', fontSize: 11, marginLeft: 6 }}>summary</button>}
                      {f.error && <div style={{ color: '#b91c1c', fontSize: 12 }}>{f.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {sharepoint && sharepoint.files?.length === 0 && (
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 8 }}>Folder is empty.</div>
          )}
        </div>
      </div>

      {summary && (
        <div style={{ ...card, marginBottom: '1rem' }}>
          <h4 style={{ margin: '0 0 0.25rem', fontSize: 15 }}>
            Reconciliation {summary.dryRun ? '(preview)' : ''}
            <span style={{ fontWeight: 400, color: '#64748b', fontSize: 13 }}> · {fmtDt(summary.importedAt)} · {summary.importedBy} · {summary.origin}</span>
          </h4>
          <SummaryTable summary={summary} onUseMaestro={useMaestro} busyJob={busyJob} />
          {summary.dryRun && file && (
            <div style={{ marginTop: 10 }}>
              <button type="button" disabled={!!busy} onClick={() => run(false)} style={btn(true, busy === 'import')}>
                {busy === 'import' ? 'Importing…' : 'Import this file'}
              </button>
            </div>
          )}
        </div>
      )}

      <div style={card}>
        <h4 style={{ margin: '0 0 0.5rem', fontSize: 15 }}>Recent runs</h4>
        {batches.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8' }}>No all-projects import yet.</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={th}>When</th><th style={th}>File</th><th style={th}>Origin</th><th style={th}>By</th>
                <th style={th}>Window</th><th style={{ ...th, textAlign: 'right' }}>Jobs</th><th style={{ ...th, textAlign: 'right' }}>Hours</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.batch_id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDt(b.imported_at)}</td>
                  <td style={td}>{b.filename}</td>
                  <td style={td}>{b.origin}</td>
                  <td style={td}>{b.imported_by}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{b.file_min_week} → {b.file_max_week}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{b.jobs_imported}/{b.jobs_in_file}{b.jobs_errored ? <span style={{ color: '#b91c1c' }}> · {b.jobs_errored} err</span> : null}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtH(b.hours_imported)}</td>
                  <td style={td}><button type="button" onClick={() => openBatch(b.batch_id)} style={{ ...btn(false), padding: '2px 8px', fontSize: 12 }}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

/**
 * Modal wrapper — opened from Admin → Settings → "Maestro Actuals (all projects)".
 */
const MaestroActualsImportModal = ({ onClose }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 10000, padding: '3vh 16px', overflowY: 'auto' }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
  >
    <div style={{ background: '#f5f7fa', borderRadius: 12, width: '100%', maxWidth: 1240, boxShadow: '0 20px 50px rgba(0,0,0,0.28)', position: 'relative', padding: '1.25rem 1.5rem 1.5rem' }}>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{ position: 'absolute', top: 12, right: 14, background: 'transparent', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#475569' }}
      >×</button>
      <MaestroActualsImportPanel />
    </div>
  </div>
);

export default MaestroActualsImportModal;
export { MaestroActualsImportPanel, GUIDE_ID as MAESTRO_ACTUALS_IMPORT_HELP_GUIDE_ID };
